import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, Store, Truck } from "lucide-react";
import Seo from "@/components/Seo";
import { CouponBox } from "@/components/CartDrawer";
import { useCart } from "@/context/CartContext";
import { apiPost, money } from "@/lib/api";

const EMPTY_FORM = {
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address_1: "",
    address_2: "",
    city: "Washington",
    state: "DC",
    postcode: "",
    customer_note: "",
};

export default function Checkout() {
    const { items, subtotal, discount, total, coupon, clearCart, setCoupon } = useCart();
    const navigate = useNavigate();
    const [form, setForm] = useState(EMPTY_FORM);
    const [fulfillment, setFulfillment] = useState("delivery");
    const [validated, setValidated] = useState(null);
    const [validateError, setValidateError] = useState(null);
    const [placing, setPlacing] = useState(false);
    const [checkoutError, setCheckoutError] = useState(null);
    const idemKey = useRef(crypto.randomUUID());

    const minimalItems = useMemo(
        () => items.map(({ product_id, variation_id, quantity }) => ({ product_id, variation_id, quantity })),
        [items]
    );

    useEffect(() => {
        if (!minimalItems.length) return;
        let live = true;
        apiPost("/cart/validate", { items: minimalItems })
            .then((res) => {
                if (live) {
                    setValidated(res);
                    setValidateError(null);
                }
            })
            .catch((e) => {
                if (live) setValidateError(e);
            });
        return () => {
            live = false;
        };
    }, [minimalItems]);

    if (!items.length) {
        return (
            <div className="container-x pt-40 pb-24 text-center" data-testid="checkout-empty">
                <h1 className="font-display text-3xl font-extrabold text-bone">Your bag is empty.</h1>
                <p className="mt-3 text-sage">Add something from the menu first.</p>
                <Link to="/shop" data-testid="checkout-empty-shop-cta" className="mt-8 inline-block rounded-full bg-leaf px-8 py-4 font-bold text-ink hover:bg-leaf-bright">
                    Shop the Menu
                </Link>
            </div>
        );
    }

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const serverSubtotal = validated?.subtotal ?? subtotal;
    const serverTotal = Math.max(serverSubtotal - discount, 0);

    const placeOrder = async () => {
        if (placing) return;
        setPlacing(true);
        setCheckoutError(null);
        try {
            const res = await apiPost("/checkout", {
                items: minimalItems,
                billing: {
                    first_name: form.first_name,
                    last_name: form.last_name,
                    email: form.email,
                    phone: form.phone,
                    address_1: form.address_1,
                    address_2: form.address_2,
                    city: form.city,
                    state: form.state,
                    postcode: form.postcode,
                    country: "US",
                },
                fulfillment,
                coupon_code: coupon?.code || "",
                customer_note: form.customer_note,
                idempotency_key: idemKey.current,
            });
            clearCart();
            navigate(`/order/${res.order_id}`, { state: res });
        } catch (e) {
            idemKey.current = crypto.randomUUID();
            setCheckoutError(e);
        } finally {
            setPlacing(false);
        }
    };

    const inputCls =
        "w-full rounded-xl border border-leaf/20 bg-moss/60 px-4 py-3.5 text-sm text-bone placeholder:text-sage/60 focus:border-leaf/60 focus:outline-none";

    return (
        <div className="pt-24 md:pt-32 pb-24" data-testid="checkout-page">
            <Seo
                title="Checkout — District Greenz DMV"
                description="Complete your District Greenz DMV order with local delivery or store pickup. Cash on delivery or pay at pickup."
                path="/checkout"
            />
            <div className="container-x max-w-6xl">
                <p className="eyebrow text-gold">Checkout</p>
                <h1 className="mt-3 font-display text-4xl md:text-6xl font-extrabold text-bone">Last step.</h1>

                <div className="mt-12 grid gap-10 lg:grid-cols-12">
                    <div className="lg:col-span-7 space-y-8">
                        <section data-testid="checkout-contact-section">
                            <h2 className="eyebrow text-leaf-bright mb-5">1 · Your details</h2>
                            <div className="grid gap-4 sm:grid-cols-2">
                                <input data-testid="checkout-first-name" className={inputCls} placeholder="First name *" value={form.first_name} onChange={set("first_name")} />
                                <input data-testid="checkout-last-name" className={inputCls} placeholder="Last name *" value={form.last_name} onChange={set("last_name")} />
                                <input data-testid="checkout-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={set("email")} />
                                <input data-testid="checkout-phone" type="tel" className={inputCls} placeholder="Phone *" value={form.phone} onChange={set("phone")} />
                            </div>
                        </section>

                        <section data-testid="checkout-fulfillment-section">
                            <h2 className="eyebrow text-leaf-bright mb-5">2 · Delivery or pickup</h2>
                            <div className="grid gap-4 sm:grid-cols-2">
                                <button
                                    data-testid="fulfillment-delivery"
                                    onClick={() => setFulfillment("delivery")}
                                    className={`flex items-start gap-4 rounded-2xl border p-5 text-left transition-colors ${
                                        fulfillment === "delivery" ? "border-leaf bg-leaf/10" : "border-leaf/15 bg-moss/40 hover:border-leaf/40"
                                    }`}
                                >
                                    <Truck size={20} className={fulfillment === "delivery" ? "text-leaf-bright" : "text-sage"} />
                                    <span>
                                        <span className="block font-display font-bold text-bone">Local Delivery</span>
                                        <span className="mt-1 block text-xs text-sage">We bring it to your door. Pay cash on arrival.</span>
                                    </span>
                                </button>
                                <button
                                    data-testid="fulfillment-pickup"
                                    onClick={() => setFulfillment("pickup")}
                                    className={`flex items-start gap-4 rounded-2xl border p-5 text-left transition-colors ${
                                        fulfillment === "pickup" ? "border-leaf bg-leaf/10" : "border-leaf/15 bg-moss/40 hover:border-leaf/40"
                                    }`}
                                >
                                    <Store size={20} className={fulfillment === "pickup" ? "text-leaf-bright" : "text-sage"} />
                                    <span>
                                        <span className="block font-display font-bold text-bone">Store Pickup</span>
                                        <span className="mt-1 block text-xs text-sage">10 Florida Ave NW · Open 24 hours, Mon–Sun · Pay at pickup.</span>
                                    </span>
                                </button>
                            </div>

                            {fulfillment === "delivery" && (
                                <div className="mt-4 grid gap-4 sm:grid-cols-2" data-testid="checkout-address-fields">
                                    <input data-testid="checkout-address-1" className={`${inputCls} sm:col-span-2`} placeholder="Street address *" value={form.address_1} onChange={set("address_1")} />
                                    <input data-testid="checkout-address-2" className={`${inputCls} sm:col-span-2`} placeholder="Apartment, suite, etc. (optional)" value={form.address_2} onChange={set("address_2")} />
                                    <input data-testid="checkout-city" className={inputCls} placeholder="City *" value={form.city} onChange={set("city")} />
                                    <input data-testid="checkout-state" className={inputCls} placeholder="State *" value={form.state} onChange={set("state")} />
                                    <input data-testid="checkout-postcode" className={inputCls} placeholder="ZIP code *" value={form.postcode} onChange={set("postcode")} />
                                </div>
                            )}
                        </section>

                        <section data-testid="checkout-payment-section">
                            <h2 className="eyebrow text-leaf-bright mb-5">3 · Payment</h2>
                            <div className="rounded-2xl border border-gold/30 bg-gold/5 p-5">
                                <p className="font-display font-bold text-bone" data-testid="checkout-payment-method">
                                    {fulfillment === "pickup" ? "Pay at pickup" : "Cash on delivery"}
                                </p>
                                <p className="mt-1 text-xs leading-relaxed text-sage">
                                    Have cash ready. A valid 21+ government-issued ID is required at hand-off — no ID, no product.
                                </p>
                            </div>
                            <textarea
                                data-testid="checkout-notes"
                                className={`${inputCls} mt-4 min-h-24`}
                                placeholder="Notes for your driver or the shop (optional)"
                                value={form.customer_note}
                                onChange={set("customer_note")}
                            />
                        </section>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="sticky top-28 space-y-5 rounded-3xl border border-leaf/15 bg-moss/40 p-6" data-testid="checkout-summary">
                            <h2 className="font-display text-xl font-extrabold text-bone">Order Summary</h2>

                            <div className="max-h-64 space-y-3 overflow-y-auto pr-1" data-testid="checkout-summary-lines">
                                {(validated?.lines || items.map((l) => ({ ...l, line_total: l.unit_price * l.quantity }))).map((l, i) => (
                                    <div key={`${l.product_id}-${l.variation_id}-${i}`} className="flex justify-between gap-3 text-sm">
                                        <span className="text-bone/80 min-w-0">
                                            <span className="font-semibold">{l.quantity}×</span> {l.name}
                                            {l.options?.length > 0 && (
                                                <span className="block text-xs text-sage">{l.options.join(" · ")}</span>
                                            )}
                                        </span>
                                        <span className="shrink-0 text-bone/80">{money(l.line_total)}</span>
                                    </div>
                                ))}
                            </div>

                            <CouponBox />

                            <div className="space-y-2 border-t border-leaf/12 pt-4 text-sm">
                                <div className="flex justify-between text-sage">
                                    <span>Subtotal</span>
                                    <span data-testid="checkout-subtotal">{money(serverSubtotal)}</span>
                                </div>
                                {discount > 0 && (
                                    <div className="flex justify-between text-leaf-bright" data-testid="checkout-discount">
                                        <span>Discount</span>
                                        <span>-{money(discount)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between border-t border-leaf/12 pt-3 font-display text-xl font-extrabold text-bone">
                                    <span>Total</span>
                                    <span data-testid="checkout-total">{money(serverTotal)}</span>
                                </div>
                            </div>

                            {serverTotal < 40 && (
                                <p className="rounded-xl border border-gold/30 bg-gold/5 p-3 text-xs text-gold" data-testid="checkout-minimum-note">
                                    Minimum order is $40 — add {money(40 - serverTotal)} more to place your order.
                                </p>
                            )}

                            {validateError && (
                                <div className="rounded-xl border border-red-400/30 bg-red-400/5 p-4 text-xs text-red-300" data-testid="checkout-validation-error">
                                    <span className="flex items-center gap-2 font-bold"><AlertTriangle size={13} /> {validateError.message}</span>
                                    {validateError.issues?.length > 0 && (
                                        <ul className="mt-2 list-disc space-y-1 pl-5">
                                            {validateError.issues.map((iss, i) => (
                                                <li key={i}>{iss.name ? `${iss.name}: ` : ""}{iss.issue}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}

                            {checkoutError && (
                                <div className="rounded-xl border border-red-400/30 bg-red-400/5 p-4 text-xs text-red-300" data-testid="checkout-error">
                                    {checkoutError.message}
                                </div>
                            )}

                            <button
                                data-testid="checkout-submit"
                                onClick={placeOrder}
                                disabled={placing || !!validateError || serverTotal < 40}
                                className="w-full rounded-full bg-leaf py-4 font-bold text-ink hover:bg-leaf-bright transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                {placing ? "Placing your order..." : serverTotal < 40 ? `Minimum order $40 — add ${money(40 - serverTotal)}` : `Place Order · ${money(serverTotal)}`}
                            </button>
                            <p className="text-center text-[11px] leading-relaxed text-sage/80">
                                By placing this order you confirm you are 21+ with valid ID. Prices are verified against the
                                live store before your order is created.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
